import { apiClient } from '../api-client';
import type { SetupItemKey, SetupStatus, SetupStatusItem } from '../../dtos/admin/setupStatus.dto';

const ITEM_KEYS: SetupItemKey[] = ['agencyProfile', 'evvChecklist', 'staffUser', 'patient'];

function normalizeItem(raw: unknown): SetupStatusItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  if (!ITEM_KEYS.includes(item.key as SetupItemKey)) return null;
  const missing = Array.isArray(item.missing)
    ? item.missing.filter((m): m is string => typeof m === 'string')
    : undefined;
  return {
    key: item.key as SetupItemKey,
    done: item.done === true,
    missing,
    link: typeof item.link === 'string' ? item.link : '',
    section: typeof item.section === 'string' && item.section ? item.section : undefined,
    adminTab: typeof item.adminTab === 'string' && item.adminTab ? item.adminTab : undefined,
    anchor: typeof item.anchor === 'string' && item.anchor ? item.anchor : null,
  };
}

/** Guided setup checklist for owners and admins (Phase 5, ACC-21). */
export class SetupStatusService {
  /** GET /agency/setup-status (403 for roles other than owner/admin) */
  static async getStatus(): Promise<SetupStatus> {
    const response = await apiClient.get<unknown>('/agency/setup-status');
    const data = (response.data && typeof response.data === 'object' ? response.data : {}) as Record<string, unknown>;
    const items = (Array.isArray(data.items) ? data.items : [])
      .map(normalizeItem)
      .filter((item): item is SetupStatusItem => item !== null);
    return {
      items,
      allDone: data.allDone === true,
      dismissedAt: typeof data.dismissedAt === 'string' ? data.dismissedAt : null,
    };
  }

  /** POST /agency/setup-status/dismiss (409 SETUP_INCOMPLETE until every item is done) */
  static async dismiss(): Promise<void> {
    await apiClient.post('/agency/setup-status/dismiss', {});
  }
}

export default SetupStatusService;
